import React, { createContext, useCallback, useMemo, useRef, useState } from "react";

/**
 * ToastProvider — lightweight in-app toast notification system.
 *
 * Why not sweetalert2 for everything? sweetalert2 is great for confirm/alert
 * modals that block interaction, but too heavy for transient notifications
 * like "Saved!" or "Connection lost". This provider is <2KB and renders a
 * stack of pills in the bottom-right corner.
 *
 * Consume via `useToast()` (see hooks/useToast.js).
 */
export const ToastContext = createContext(null);

let nextId = 1;

const COLORS = {
  success: "bg-green-600",
  error: "bg-red-600",
  warning: "bg-amber-500",
  info: "bg-blue-600",
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const push = useCallback(
    (kind, message, options = {}) => {
      const id = nextId++;
      const ttl = options.ttl ?? 4000;
      setToasts((prev) => [...prev, { id, kind, message }]);
      if (ttl > 0) {
        const timer = setTimeout(() => dismiss(id), ttl);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss]
  );

  const api = useMemo(() => ({
    success: (msg, opts) => push("success", msg, opts),
    error: (msg, opts) => push("error", msg, opts),
    warning: (msg, opts) => push("warning", msg, opts),
    info: (msg, opts) => push("info", msg, opts),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`${COLORS[t.kind] || COLORS.info} text-white rounded-lg shadow-lg px-4 py-3 min-w-[240px] max-w-sm text-sm flex items-start gap-3 pointer-events-auto animate-[slideIn_.2s_ease-out]`}
            role="status"
          >
            <span className="flex-1 break-words">{t.message}</span>
            <button
              onClick={() => dismiss(t.id)}
              className="opacity-80 hover:opacity-100 text-white text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
